import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import {
  BiArrowBack,
  BiBarChartAlt2,
  BiBuildings,
  BiCalendarCheck,
  BiCheckShield,
  BiGitBranch,
  BiGroup,
  BiIdCard,
  BiPieChartAlt2,
  BiRefresh,
  BiRupee,
  BiSend,
  BiSpreadsheet,
  BiTimeFive,
  BiTrophy
} from 'react-icons/bi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { parivartanAPI } from '../../../services/platformAPI';
import { downloadWorkbook } from '../../../utils/downloadWorkbook';
import { formatWorkspaceDate } from '../../../utils/workspaceDates';
import { useWorkspaceAccess } from '../../../hooks/useWorkspaceAccess';
import { cellText, describeReport, facetCounts, rowMatches, sortRows } from './reportCriteria';
import './ReportsPage.scss';

const EMPTY_CRITERIA = { query: '', picked: {}, datePreset: 'any', dateFrom: '', dateTo: '', band: 'any' };

const criteriaActive = (criteria) => Boolean(
  criteria.query.trim()
  || criteria.datePreset !== 'any'
  || criteria.dateFrom
  || criteria.dateTo
  || (criteria.band && criteria.band !== 'any')
  || Object.values(criteria.picked || {}).some((values) => values?.length)
);

const inr = (n, currency = 'INR') => {
  const value = Number(n || 0);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString('en-IN')}`;
  }
};

const inrPaise = (paise, currency = 'INR') => inr(Number(paise || 0) / 100, currency);

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return formatWorkspaceDate(d);
};

const labelize = (v) =>
  String(v || '—')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const MARK_KEYS = new Set(['stage', 'status', 'outcome', 'bucket', 'humanDecision', 'approvalStatus', 'reviewState', 'type', 'priority', 'workType', 'context', 'dimension']);
const MONEY_KEYS = new Set(['estimatedValue', 'value', 'openValue', 'awardedValue', 'totalCostPaise', 'indicativePricePaise', 'effortCostPaise']);

const columnKind = (column) => {
  if (column.key === 'reference') return 'code';
  if (column.key === 'title') return 'title';
  if (MARK_KEYS.has(column.key)) return 'mark';
  if (MONEY_KEYS.has(column.key) || column.key === 'hours' || column.key === 'plannedHours' || column.key === 'approvedHours' || column.key === 'varianceHours') return 'money';
  if (/deadline|date|validto|submittedat|periodstart|duedate/i.test(column.key)) return 'date';
  if (column.key === 'organization' || column.key === 'personName' || column.key === 'personnelName') return 'client';
  return 'text';
};

const markTone = (value) => {
  const text = String(value || '').toLowerCase();
  if (/lost|no-go|no_go|overdue|expired|returned|reject|withdrawn|cancelled/.test(text)) return 'stop';
  if (text === 'go' || /award|won|approved/.test(text)) return 'good';
  if (/submit|pending|due|assess/.test(text)) return 'wait';
  return 'open';
};

const dateIsPast = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

const REPORTS = [
  {
    id: 'pipeline',
    title: 'Pipeline overview',
    description: 'Pursuits by stage, value, and how work moves from capture to close.',
    icon: BiGitBranch,
    lens: { bar: 'stage', extras: ['sector', 'serviceType'] },
    fetch: () => parivartanAPI.reportPipeline(),
    kpis: (d) => [
      { label: 'Pursuits', value: d.total },
      { label: 'Open value', value: inr(d.openValue) },
      { label: 'Stages', value: Object.keys(d.byStage || {}).length },
      { label: 'Needs review', value: d.needsReview || 0 }
    ],
    columns: [
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'organization', label: 'Client' },
      { key: 'stage', label: 'Stage', render: (r) => labelize(r.stage) },
      { key: 'reviewNote', label: 'Review', render: (r) => r.reviewNote || '—' },
      { key: 'estimatedValue', label: 'Value', render: (r) => inr(r.estimatedValue, r.currency) },
      { key: 'deadline', label: 'Deadline', render: (r) => fmtDate(r.deadline) }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'work-packages',
    title: 'Work packages',
    description: 'Preparation, HR / Resources, Finance and Costing. The done count does not approve submission.',
    icon: BiSpreadsheet,
    lens: { bar: 'status', extras: ['package', 'owner', 'reference'] },
    fetch: () => parivartanAPI.reportWorkPackages(),
    kpis: (d) => [
      { label: 'Packages', value: d.total },
      { label: 'Overdue', value: d.overdue || 0 }
    ],
    columns: [
      { key: 'reference', label: 'Tender' },
      { key: 'title', label: 'Title' },
      { key: 'package', label: 'Package' },
      { key: 'owner', label: 'Owner' },
      { key: 'dueOn', label: 'Due date' },
      { key: 'status', label: 'Status', render: (r) => labelize(r.status) },
      { key: 'blocker', label: 'Blocker', render: (r) => r.blocker || '—' },
      { key: 'overdueAge', label: 'Overdue age', render: (r) => (r.overdueAge === '' || r.overdueAge == null ? '—' : `${r.overdueAge} calendar days`) }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'fee-emd',
    title: 'Fee, EMD and BG',
    description: 'Tender fee, cash EMD, cash held as performance security, bank guarantee exposure, and physical submission dates. Cash outstanding, cash held, and guarantees stay in separate totals.',
    icon: BiSpreadsheet,
    lens: { bar: 'line', extras: ['currency', 'status', 'reference'] },
    fetch: () => parivartanAPI.reportFeeEmd(),
    kpis: (d) => [
      { label: 'Lines', value: d.rows?.length || 0 },
      { label: 'Cash outstanding', value: (d.cashByCurrency || []).map((row) => `${row.currency} ${row.amount}`).join(' · ') || '0' },
      { label: 'Cash held', value: (d.cashHeldByCurrency || []).map((row) => `${row.currency} ${row.amount}`).join(' · ') || '0' },
      { label: 'Guarantee currencies', value: (d.guaranteeByCurrency || []).map((row) => `${row.currency} ${row.amount}`).join(' · ') || '0' }
    ],
    columns: [
      { key: 'reference', label: 'Tender' },
      { key: 'title', label: 'Title' },
      { key: 'line', label: 'Line' },
      { key: 'currency', label: 'Currency' },
      { key: 'amount', label: 'Amount' },
      { key: 'status', label: 'Status' },
      { key: 'cashOutstanding', label: 'Cash outstanding' },
      { key: 'cashHeld', label: 'Cash held' },
      { key: 'guaranteeExposure', label: 'Guarantee exposure' },
      { key: 'physicalDueOn', label: 'Physical due' },
      { key: 'onlineDueOn', label: 'Online due' },
      { key: 'refundAge', label: 'Refund age' }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'team',
    title: 'Delivery team',
    description: 'The current team version for each tender: role, named person, consent and conflict readiness, version, submitter, approver, and decision date. CV and consent document references stay hidden unless you can see personnel facts.',
    icon: BiGroup,
    lens: { bar: 'status', extras: ['reference', 'usable'] },
    fetch: () => parivartanAPI.reportTeam(),
    kpis: (d) => [
      { label: 'Rows', value: d.rows?.length || 0 },
      { label: 'Approved rows', value: (d.rows || []).filter((row) => row.usable).length }
    ],
    columns: [
      { key: 'reference', label: 'Tender' },
      { key: 'role', label: 'Role' },
      { key: 'person', label: 'Person' },
      { key: 'consent', label: 'Consent' },
      { key: 'conflict', label: 'Conflict' },
      { key: 'readiness', label: 'Readiness' },
      { key: 'version', label: 'Version' },
      { key: 'status', label: 'Status', render: (r) => labelize(r.status) },
      { key: 'usable', label: 'Current approval', render: (r) => (r.usable ? 'Approved' : 'Not the approved team') },
      { key: 'submitter', label: 'Submitter' },
      { key: 'approver', label: 'Approver' },
      { key: 'decidedOn', label: 'Decision date' }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'win-loss',
    title: 'Win / loss',
    description: 'Awarded, lost, withdrawn, cancelled, and No-Go tenders, with the reason recorded for each.',
    icon: BiTrophy,
    lens: { bar: 'outcome', extras: ['stage'] },
    fetch: () => parivartanAPI.reportWinLoss(),
    kpis: (d) => [
      { label: 'Won', value: d.won },
      { label: 'Lost', value: d.lost },
      { label: 'Withdrawn', value: d.withdrawn || 0 },
      { label: 'Cancelled', value: d.cancelled || 0 },
      { label: 'No-Go', value: d.noGo || 0 },
      { label: 'Win rate', value: `${d.winRate || 0}%` },
      { label: 'Won value', value: inr(d.wonValue) },
      { label: 'Needs review', value: d.needsReview || 0 }
    ],
    columns: [
      { key: 'outcome', label: 'Outcome', render: (r) => labelize(r.outcome) },
      { key: 'reviewNote', label: 'Review', render: (r) => r.reviewNote || '—' },
      { key: 'outcomeReason', label: 'Reason', render: (r) => r.outcomeReason || '—' },
      { key: 'outcomeRemarks', label: 'Remarks', render: (r) => r.outcomeRemarks || '—' },
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'organization', label: 'Client' },
      { key: 'estimatedValue', label: 'Value', render: (r) => inr(r.estimatedValue, r.currency) }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'deadlines',
    title: 'Deadline watch',
    description: 'Submission and clarification dates that are due, overdue, or coming up.',
    icon: BiCalendarCheck,
    lens: { bar: 'bucket', extras: ['organization', 'stage'] },
    fetch: () => parivartanAPI.reportDeadlines(),
    kpis: (d) => [
      { label: 'Overdue', value: d.overdue },
      { label: 'Due in 7 days', value: d.dueSoon },
      { label: 'Upcoming (45d)', value: d.upcoming }
    ],
    columns: [
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'organization', label: 'Client' },
      { key: 'deadline', label: 'Submission', render: (r) => fmtDate(r.deadline) },
      { key: 'daysRemaining', label: 'Days', render: (r) => r.daysRemaining },
      { key: 'bucket', label: 'Status', render: (r) => labelize(r.bucket) },
      { key: 'clarificationDeadline', label: 'Clarification', render: (r) => fmtDate(r.clarificationDeadline) }
    ],
    rows: (d) => d.deadlines || []
  },
  {
    id: 'approvals',
    title: 'Pending approvals',
    description: 'Technical, commercial, and bid approvals still waiting for a decision.',
    icon: BiCheckShield,
    lens: { bar: 'status', extras: [] },
    fetch: () => parivartanAPI.reportPendingApprovals(),
    kpis: (d) => [
      { label: 'Open items', value: d.total },
      { label: 'Types', value: Object.keys(d.byType || {}).length }
    ],
    columns: [
      { key: 'title', label: 'Item' },
      { key: 'type', label: 'Type', render: (r) => labelize(r.type) },
      { key: 'status', label: 'Status', render: (r) => labelize(r.status) },
      { key: 'priority', label: 'Priority', render: (r) => labelize(r.priority) },
      { key: 'dueDate', label: 'Due', render: (r) => fmtDate(r.dueDate) }
    ],
    rows: (d) => d.workItems || []
  },
  {
    id: 'decisions',
    title: 'Go / no-go',
    description: 'Decisions taken, reviews still open, and pursuits stopped at no-go.',
    icon: BiBarChartAlt2,
    lens: { bar: 'humanDecision', extras: ['organization', 'status'] },
    fetch: () => parivartanAPI.reportGoNoGo(),
    kpis: (d) => [
      { label: 'Reviews', value: d.total },
      { label: 'Go', value: d.byDecision?.go || 0 },
      { label: 'No-go', value: d.byDecision?.no_go || 0 },
      { label: 'Pending', value: d.byDecision?.pending || 0 }
    ],
    columns: [
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'organization', label: 'Client' },
      { key: 'humanDecision', label: 'Decision', render: (r) => labelize(r.humanDecision) },
      { key: 'outcomeReason', label: 'Reason', render: (r) => r.outcomeReason || '—' },
      { key: 'outcomeRemarks', label: 'Remarks', render: (r) => r.outcomeRemarks || '—' },
      { key: 'recommendation', label: 'Recommendation', render: (r) => labelize(r.recommendation) },
      { key: 'decisionDate', label: 'Decided', render: (r) => fmtDate(r.decisionDate) }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'credentials',
    title: 'Credential expiry',
    description: 'Registrations, certificates, and insurance that need renewal before a bid.',
    icon: BiIdCard,
    lens: { bar: 'approvalStatus', extras: [] },
    fetch: () => parivartanAPI.reportCredentialExpiry(),
    kpis: (d) => [
      { label: 'Expired', value: d.expired },
      { label: 'Within 30 days', value: d.within30 },
      { label: 'Within 90 days', value: d.within90 }
    ],
    columns: [
      { key: 'title', label: 'Credential' },
      { key: 'category', label: 'Category' },
      { key: 'validTo', label: 'Valid to', render: (r) => fmtDate(r.validTo) },
      { key: 'daysToExpiry', label: 'Days left' },
      { key: 'approvalStatus', label: 'Status', render: (r) => labelize(r.approvalStatus) }
    ],
    rows: (d) => d.credentials || []
  },
  {
    id: 'effort',
    title: 'Effort variance',
    description: 'Baseline hours against approved actual hours. Tender-stage and assignment-stage effort stay separate. Cost is an internal effort-cost estimate.',
    icon: BiTimeFive,
    lens: { bar: 'stage', extras: [] },
    fetch: () => parivartanAPI.reportEffortVariance(),
    kpis: (d) => [
      { label: 'Planned', value: `${d.plannedHours || 0}h` },
      { label: 'Approved', value: `${d.approvedHours || 0}h` },
      { label: 'Pending', value: `${d.pendingHours || 0}h` },
      { label: 'Variance', value: `${d.varianceHours || 0}h` },
      { label: 'Missing rates', value: d.missingRates || 0 },
      { label: 'Incomplete cost', value: d.incompleteCosts || 0 }
    ],
    columns: [
      { key: 'stage', label: 'Stage', render: (r) => (r.stage === 'assignment' ? 'Assignment' : 'Tender') },
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'plannedHours', label: 'Baseline hours' },
      { key: 'approvedHours', label: 'Approved hours' },
      { key: 'varianceHours', label: 'Hours variance' },
      { key: 'hoursVariancePercent', label: 'Hours %' },
      { key: 'costIncomplete', label: 'Cost', render: (r) => (r.costIncomplete ? 'Incomplete' : (r.costVariancePercent || '—')) }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'timesheets',
    title: 'Timesheet status',
    description: 'Draft, submitted, returned, and approved time. Only approved hours count as actual effort.',
    icon: BiSpreadsheet,
    lens: { bar: 'status', extras: [] },
    fetch: () => parivartanAPI.reportTimesheetStatus(),
    kpis: (d) => [
      { label: 'Sheets', value: d.timesheets },
      { label: 'Approved hours', value: d.approvedHours },
      { label: 'Pending hours', value: d.pendingHours },
      { label: 'Missing rates', value: d.missingRates || 0 }
    ],
    columns: [
      { key: 'personName', label: 'Employee' },
      { key: 'workType', label: 'Work', render: (r) => (r.workType === 'assignment' ? 'Assignment' : 'Tender') },
      { key: 'activity', label: 'Activity' },
      { key: 'status', label: 'Status', render: (r) => labelize(r.status) },
      { key: 'hours', label: 'Hours' },
      { key: 'periodStart', label: 'Date', render: (r) => fmtDate(r.periodStart) },
      { key: 'effortCostPaise', label: 'Effort cost', render: (r) => (r.effortCostPaise == null ? (r.rateMissing ? 'Rate missing' : '—') : inrPaise(r.effortCostPaise)) }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'commercials',
    title: 'Commercial summary',
    description: 'Cost estimates, rates, and indicative price for pursuits in preparation.',
    icon: BiRupee,
    lens: { bar: 'status', extras: ['organization'] },
    fetch: () => parivartanAPI.reportCommercials(),
    kpis: (d) => [
      { label: 'Estimates', value: d.estimateCount },
      { label: 'Approved cost', value: inrPaise(d.approvedCostPaise) },
      { label: 'Indicative price', value: inrPaise(d.indicativePricePaise) },
      { label: 'Uncosted', value: d.uncostedEstimates },
      { label: 'Unclassified rates', value: d.unclassifiedRateCount || 0 }
    ],
    columns: [
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'organization', label: 'Client' },
      { key: 'version', label: 'Ver' },
      { key: 'status', label: 'Status', render: (r) => labelize(r.status) },
      { key: 'totalCostPaise', label: 'Cost', render: (r) => (r.costIncomplete ? `Incomplete · ${r.uncostedLineCount} without a rate` : inrPaise(r.totalCostPaise, r.currency)) },
      { key: 'indicativePricePaise', label: 'Price', render: (r) => (r.costIncomplete ? 'Incomplete' : inrPaise(r.indicativePricePaise, r.currency)) },
      { key: 'uncostedLineCount', label: 'Uncosted' }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'submissions',
    title: 'Submission register',
    description: 'Bids sent to the client, with portal reference and pack status.',
    icon: BiSend,
    lens: { bar: 'stage', extras: ['organization', 'stage'] },
    fetch: () => parivartanAPI.reportSubmissions(),
    kpis: (d) => [
      { label: 'Submitted', value: d.total },
      { label: 'With portal ref', value: d.withPortalRef },
      { label: 'Open clarifications', value: d.openClarifications }
    ],
    columns: [
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'organization', label: 'Client' },
      { key: 'stage', label: 'Stage', render: (r) => labelize(r.stage) },
      { key: 'submittedAt', label: 'Submitted', render: (r) => fmtDate(r.submittedAt) },
      { key: 'portalReference', label: 'Portal ref', render: (r) => r.portalReference || '—' },
      {
        key: 'checklist',
        label: 'Checklist',
        render: (r) => `${r.checklistDone || 0}/${r.checklistTotal || 0}`
      },
      { key: 'clarifications', label: 'Clarifications' }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'personnel',
    title: 'Personnel deployment',
    description: 'Named staff on live pursuits and on awarded assignments.',
    icon: BiGroup,
    lens: { bar: 'context', extras: ['reviewState'] },
    fetch: () => parivartanAPI.reportPersonnelAllocation(),
    kpis: (d) => [
      { label: 'Named people', value: d.namedPeople },
      { label: 'On pursuits', value: d.pursuitCount },
      { label: 'Assignments', value: d.assignmentCount }
    ],
    columns: [
      { key: 'context', label: 'Context', render: (r) => labelize(r.context) },
      { key: 'personnelName', label: 'Person' },
      { key: 'designation', label: 'Role' },
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Tender' },
      { key: 'effortHours', label: 'Hours', render: (r) => r.effortHours ?? '—' },
      { key: 'reviewState', label: 'State', render: (r) => labelize(r.reviewState) }
    ],
    rows: (d) => d.rows || []
  },
  {
    id: 'mix',
    title: 'Client and sector mix',
    description: 'Pipeline value grouped by client, sector, and service line.',
    icon: BiPieChartAlt2,
    lens: { bar: 'dimension', extras: [] },
    fetch: () => parivartanAPI.reportClientMix(),
    kpis: (d) => [
      { label: 'Total value', value: inr(d.totalValue) },
      { label: 'Open value', value: inr(d.openValue) },
      { label: 'Clients', value: d.clientCount }
    ],
    columns: [
      { key: 'dimension', label: 'Group' },
      { key: 'key', label: 'Name' },
      { key: 'count', label: 'Count' },
      { key: 'value', label: 'Value', render: (r) => inr(r.value) }
    ],
    rows: (d) => [
      ...(d.byClient || []).slice(0, 15).map((r) => ({ ...r, dimension: 'Client' })),
      ...(d.bySector || []).slice(0, 10).map((r) => ({ ...r, dimension: 'Sector' })),
      ...(d.byService || []).slice(0, 10).map((r) => ({ ...r, dimension: 'Service' }))
    ]
  },
  {
    id: 'clients',
    title: 'Client concentration',
    description: 'How much open and awarded work sits with each client.',
    icon: BiBuildings,
    lens: { bar: 'key', extras: [] },
    fetch: () => parivartanAPI.reportClientMix(),
    kpis: (d) => [
      { label: 'Open value', value: inr(d.openValue) },
      { label: 'Awarded value', value: inr(d.awardedValue) },
      { label: 'Clients', value: d.clientCount }
    ],
    columns: [
      { key: 'key', label: 'Client' },
      { key: 'openValue', label: 'Open', render: (r) => inr(r.openValue) },
      { key: 'awardedValue', label: 'Awarded', render: (r) => inr(r.awardedValue) },
      { key: 'count', label: 'Pursuits' }
    ],
    rows: (d) => {
      const openMap = Object.fromEntries((d.openByClient || []).map((r) => [r.key, r]));
      const awardedMap = Object.fromEntries((d.awardedByClient || []).map((r) => [r.key, r]));
      return (d.byClient || []).map((r) => ({
        key: r.key,
        count: r.count,
        openValue: openMap[r.key]?.value || 0,
        awardedValue: awardedMap[r.key]?.value || 0
      }));
    }
  }
];

const reportById = Object.fromEntries(REPORTS.map((r) => [r.id, r]));

const ReportCriteria = ({ rows, spec, criteria, narrowed, shownCount, onCriteria, onToggle, onFacet }) => {
  const barFacet = spec.facets.find((facet) => facet.key === spec.bar && facet.options.length <= 8)
    || spec.facets.find((facet) => facet.options.length <= 8)
    || null;
  const chipFacets = spec.facets.filter((facet) => facet !== barFacet && facet.options.length <= 6);
  const selectFacets = spec.facets.filter((facet) => facet !== barFacet && facet.options.length > 6);
  const patch = (next) => onCriteria((current) => ({ ...current, ...next }));
  const clear = () => onCriteria(EMPTY_CRITERIA);

  return (
    <section className="rpt-criteria" aria-label="Report criteria">
      <div className="rpt-criteria__search">
        <input
          type="search"
          value={criteria.query}
          placeholder="Search reference, title, client, or any column"
          aria-label="Search this report"
          onChange={(event) => patch({ query: event.target.value })}
        />
        {narrowed ? (
          <button type="button" onClick={clear}>Clear</button>
        ) : null}
        <span>{shownCount} of {rows.length}</span>
      </div>

      {barFacet ? (
        <div className="rpt-share" role="group" aria-label={barFacet.label}>
          {barFacet.options.map((option) => {
            const counts = facetCounts(rows, spec.columns, spec, criteria, barFacet);
            const on = (criteria.picked[barFacet.key] || []).includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className={on ? 'is-on' : ''}
                style={{ flexGrow: Math.max(counts[option.value] || 0, 1) }}
                aria-pressed={on}
                onClick={() => onToggle(barFacet.key, option.value)}
              >
                <strong>{counts[option.value] || 0}</strong>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="rpt-criteria__deck">
        <div className="rpt-criteria__picks">
        {chipFacets.map((facet) => {
          const counts = facetCounts(rows, spec.columns, spec, criteria, facet);
          return (
            <div key={facet.key} className="rpt-criteria__group">
              <span>{facet.label}</span>
              {facet.options.map((option) => {
                const on = (criteria.picked[facet.key] || []).includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={on ? 'is-on' : ''}
                    aria-pressed={on}
                    onClick={() => onToggle(facet.key, option.value)}
                  >
                    {option.label}
                    <em>{counts[option.value] || 0}</em>
                  </button>
                );
              })}
            </div>
          );
        })}

        {selectFacets.length ? (
          <div className="rpt-criteria__selects">
            {selectFacets.map((facet) => (
              <label key={facet.key} className="rpt-criteria__select">
                {facet.label}
                <select
                  value={(criteria.picked[facet.key] || [])[0] || ''}
                  onChange={(event) => onFacet(facet.key, event.target.value)}
                >
                  <option value="">All</option>
                  {facet.options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        ) : null}
        </div>

        <div className="rpt-criteria__ranges">
        {spec.date ? (
          <div className="rpt-criteria__group">
            <span>{spec.date.label}</span>
            {[
              ['any', 'Any time'],
              ['overdue', 'Overdue'],
              ['next30', 'Next 30 days'],
              ['next90', 'Next 90 days']
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={criteria.datePreset === id && !criteria.dateFrom && !criteria.dateTo ? (id === 'any' ? 'is-quiet' : 'is-on') : ''}
                aria-pressed={criteria.datePreset === id}
                onClick={() => patch({ datePreset: id, dateFrom: '', dateTo: '' })}
              >
                {label}
              </button>
            ))}
            <label>
              From
              <input type="date" value={criteria.dateFrom} onChange={(event) => patch({ datePreset: 'range', dateFrom: event.target.value })} />
            </label>
            <label>
              To
              <input type="date" value={criteria.dateTo} onChange={(event) => patch({ datePreset: 'range', dateTo: event.target.value })} />
            </label>
          </div>
        ) : null}

        {spec.value?.bands?.length > 1 ? (
          <div className="rpt-criteria__group">
            <span>{spec.value.label}</span>
            <button
              type="button"
              className={!criteria.band || criteria.band === 'any' ? 'is-quiet' : ''}
              aria-pressed={!criteria.band || criteria.band === 'any'}
              onClick={() => patch({ band: 'any' })}
            >
              Any amount
            </button>
            {spec.value.bands.map((band) => (
              <button
                key={band.id}
                type="button"
                className={criteria.band === band.id ? 'is-on' : ''}
                aria-pressed={criteria.band === band.id}
                onClick={() => patch({ band: band.id })}
              >
                {band.label}
              </button>
            ))}
          </div>
        ) : null}
        </div>
      </div>
    </section>
  );
};

const ReportsPage = () => {
  const { allowed } = useWorkspaceAccess();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get('report') || '';
  const report = reportById[activeId] || null;
  useDocumentTitle(report ? report.title : 'Reports');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
  const [sort, setSort] = useState({ key: '', dir: 'asc' });

  const openReport = (id) => setSearchParams(id ? { report: id } : {});
  const closeReport = () => setSearchParams({});

  const load = useCallback(() => {
    if (!report) {
      setData(null);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    report
      .fetch()
      .then((res) => setData(res.data?.data || null))
      .catch(() => {
        setData(null);
        setError('Unable to load this report right now.');
      })
      .finally(() => setLoading(false));
  }, [report]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setCriteria(EMPTY_CRITERIA);
    setSort({ key: '', dir: 'asc' });
  }, [activeId]);

  const rows = useMemo(() => (report && data ? report.rows(data) : []), [report, data]);
  const kpis = useMemo(() => (report && data ? report.kpis(data) : []), [report, data]);
  const spec = useMemo(
    () => (report ? describeReport(report.columns, rows, report.lens?.extras || [], report.lens?.bar || '') : null),
    [report, rows]
  );
  const shown = useMemo(() => {
    if (!report || !spec) return [];
    const matched = rows.filter((row) => rowMatches(row, spec.columns, spec, criteria));
    return sortRows(matched, sort);
  }, [report, rows, spec, criteria, sort]);
  const narrowed = criteriaActive(criteria);

  const exportExcel = () => {
    if (!report) return;
    const sheetRows = shown.map((row) => {
      const out = {};
      report.columns.forEach((col) => {
        const text = cellText(col, row);
        out[col.label] = text || (row[col.key] == null || typeof row[col.key] === 'object' ? '' : row[col.key]);
      });
      return out;
    });
    downloadWorkbook(report.id || 'report', [{ name: report.title, rows: sheetRows }]);
  };

  const toggleFacet = (key, value) => {
    setCriteria((current) => {
      const list = current.picked[key] || [];
      const next = list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
      return { ...current, picked: { ...current.picked, [key]: next } };
    });
  };

  const setFacet = (key, value) => {
    setCriteria((current) => ({
      ...current,
      picked: { ...current.picked, [key]: value ? [value] : [] }
    }));
  };

  const toggleSort = (key) => {
    setSort((current) => (
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    ));
  };

  const openTender = (row) => {
    const tenderOnlyIds = new Set(['pipeline', 'deadlines', 'win-loss']);
    const id = row?.tenderId || (tenderOnlyIds.has(report?.id) ? row?._id : null);
    if (!id) return;
    navigate(`/apps/tender/register/${id}`);
  };

  if (report) {
    return (
      <div className="rpt-view">
        <WorkspacePageHeader
          title={report.title}
          help="reports"
          subtitle={report.description}
          actions={(
            <>
              <Button size="sm" variant="outline-secondary" onClick={closeReport}>
                <BiArrowBack className="me-1" />
                All reports
              </Button>
              <Button size="sm" variant="outline-primary" onClick={load} disabled={loading}>
                <BiRefresh className="me-1" />
                Refresh
              </Button>
              <Button size="sm" variant="outline-primary" onClick={exportExcel} disabled={!shown.length || !allowed(['reports.download', 'tender.reports.read'])}>
                Export Excel
              </Button>
            </>
          )}
        />

        {error ? <Alert variant="warning">{error}</Alert> : null}

        {loading && !data ? (
          <div className="rpt-view__loading">
            <Spinner animation="border" size="sm" />
          </div>
        ) : null}

        {data ? (
          <>
            <div className="rpt-view__kpis">
              {kpis.map((kpi) => (
                <div key={kpi.label} className="rpt-view__kpi">
                  <span>{kpi.label}</span>
                  <strong>{kpi.value ?? '—'}</strong>
                </div>
              ))}
            </div>

            {spec ? (
              <ReportCriteria
                rows={rows}
                spec={spec}
                criteria={criteria}
                narrowed={narrowed}
                shownCount={shown.length}
                onCriteria={setCriteria}
                onToggle={toggleFacet}
                onFacet={setFacet}
              />
            ) : null}

            <div className="rpt-view__panel">
              <div className="rpt-view__panel-head">
                <h2>Live results</h2>
                <span>{narrowed ? `${shown.length} of ${rows.length} rows` : `${rows.length} row${rows.length === 1 ? '' : 's'}`}</span>
              </div>
              {rows.length === 0 ? (
                <p className="rpt-view__empty">No rows for this report yet.</p>
              ) : shown.length === 0 ? (
                <p className="rpt-view__empty">Nothing matches these criteria. Clear them to see the full report.</p>
              ) : (
                <div className="rpt-view__table-wrap">
                  <Table size="sm" hover className="rpt-view__table mb-0">
                    <thead>
                      <tr>
                        {report.columns.map((col) => (
                          <th
                            key={col.key}
                            className={`rpt-cell rpt-cell--${columnKind(col)}`}
                            aria-sort={sort.key === col.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          >
                            <button type="button" onClick={() => toggleSort(col.key)}>
                              {col.label}
                              {sort.key === col.key ? <span>{sort.dir === 'asc' ? ' ↑' : ' ↓'}</span> : null}
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((row, idx) => (
                        <tr
                          key={row._id || `${row.key || row.reference || 'row'}-${idx}`}
                          className={row.tenderId || row.reference ? 'is-linkish' : undefined}
                          onClick={() => openTender(row)}
                        >
                          {report.columns.map((col) => {
                            const kind = columnKind(col);
                            const text = col.render ? col.render(row) : (row[col.key] ?? '—');
                            const past = kind === 'date' && dateIsPast(row[col.key]);
                            return (
                              <td key={col.key} className={`rpt-cell rpt-cell--${kind}${past ? ' is-past' : ''}`}>
                                {kind === 'mark' ? (
                                  <span className={`rpt-mark rpt-mark--${markTone(row[col.key] || text)}`}>{text}</span>
                                ) : text}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rpt-catalog">
      <WorkspacePageHeader
        title="Reports"
        help="reports"
        subtitle="Live management reports for tender pursuit, approvals, commercials, and delivery. Open a report, then narrow it by stage, date, value, or search."
      />
      <div className="rpt-catalog__grid">
        {REPORTS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              className="rpt-catalog__card"
              onClick={() => openReport(item.id)}
            >
              <div className="rpt-catalog__card-head">
                <span className="rpt-catalog__icon" aria-hidden>
                  <Icon />
                </span>
                <h2>{item.title}</h2>
              </div>
              <p>{item.description}</p>
              <span className="rpt-catalog__cta">Open report</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsPage;
